// Lightweight, dependency-free i18n content for the institutional site.
// Add a locale by extending `Locale` and providing a matching `Translation`.

export type Locale = 'en' | 'pt' | 'es';

export type FeatureKey =
  | 'email'
  | 'gamesList'
  | 'practiceHome'
  | 'dailyTraining'
  | 'openingPractice'
  | 'puzzles'
  | 'analytics';

export interface FeatureCopy {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
}

export interface ShowcaseCopy {
  eyebrow: string;
  title: string;
  description: string;
  bullets: string[];
}

export interface Translation {
  localeLabel: string;
  header: {
    tagline: string;
    howItWorks: string;
    privacy: string;
    support: string;
    terms: string;
    signIn: string;
    createAccount: string;
    languageLabel: string;
  };
  hero: {
    chip: string;
    titleLine1: string;
    titleLine2: string;
    subtitle: string;
    getIos: string;
    getAndroid: string;
    signInWeb: string;
    createWebAccount: string;
  };
  featuresSection: {
    title: string;
    subtitle: string;
  };
  features: Record<FeatureKey, FeatureCopy>;
  coach: ShowcaseCopy;
  tournaments: ShowcaseCopy;
  footer: {
    rights: string;
    howItWorks: string;
    privacy: string;
    support: string;
    terms: string;
  };
  howItWorks: {
    title: string;
    backToGuides: string;
    backHome: string;
  };
}

const en: Translation = {
  localeLabel: 'English',
  header: {
    tagline: 'Analysis that turns your games into training',
    howItWorks: 'How it works',
    privacy: 'Privacy',
    support: 'Support',
    terms: 'Terms',
    signIn: 'Sign in',
    createAccount: 'Create account',
    languageLabel: 'Language'
  },
  hero: {
    chip: 'Built around your own games',
    titleLine1: 'Improve your tactics and strategy',
    titleLine2: 'using your games.',
    subtitle:
      'ChessWiz turns every game into targeted training with mistake alarms, a strategy coach, personalized puzzles, daily plans, and analytics that actually tell you what to work on next.',
    getIos: 'Get the iOS app',
    getAndroid: 'Get the Android app',
    signInWeb: 'Sign in on the web',
    createWebAccount: 'Create a web account'
  },
  featuresSection: {
    title: 'Training that targets the reason games slip away',
    subtitle:
      'ChessWiz turns your recurring mistakes, opening gaps, and missed chances into focused practice loops you can actually repeat.'
  },
  features: {
    email: {
      eyebrow: 'Email and push alerts for your errors',
      title: 'Mistake alerts by email',
      description:
        'Receive emails and push notifications for errors in your games, with the move, eval swing, and position so you know exactly what to fix.',
      bullets: ['Email and push alerts', 'Before / after eval', 'Position snapshot']
    },
    gamesList: {
      eyebrow: 'Every game, scannable in a row',
      title: 'Match history at a glance',
      description:
        'Each game in your history shows the full eval timeline, accuracy, and move-quality breakdown so you can spot the games worth studying without opening them.',
      bullets: ['Inline eval graph', 'Accuracy & result chips', 'Move-quality counts']
    },
    practiceHome: {
      eyebrow: 'Multiple puzzle types from your games',
      title: 'Practice home',
      description:
        'Enjoy multiple types of puzzles based on your games, from engine mistakes and evaluation drills to openings, streaks, and lessons learned.',
      bullets: ['Engine mistake puzzles', 'Evaluation drills', 'Opening and streak practice']
    },
    dailyTraining: {
      eyebrow: 'A tailored plan from your latest games',
      title: 'Daily training',
      description:
        'Every day ChessWiz builds a focused session from your most recent games — the exact mistakes, missed wins, and weak moments worth revisiting, queued into one deck.',
      bullets: ['Curated daily deck', 'Straight from your games', 'One tap to start']
    },
    openingPractice: {
      eyebrow: 'Repetition built around your repertoire',
      title: 'Opening practice',
      description:
        'Reinforce move orders and ideas from the openings you actually play instead of drilling generic theory.',
      bullets: ['Repertoire drills', 'Line recall practice', 'Variation coverage']
    },
    puzzles: {
      eyebrow: 'Tactics from positions you recognize',
      title: 'Puzzles based on your games',
      description:
        'Generate puzzles from your own mistakes, wins, and missed opportunities so calculation practice maps back to real games.',
      bullets: ['Personalized puzzle sets', 'Missed-win recovery', 'Pattern retention']
    },
    analytics: {
      eyebrow: 'Track progress with useful signals',
      title: 'Analytics that matters',
      description:
        'See which openings, phases, and mistake patterns are holding you back, then train exactly where rating gains are hiding.',
      bullets: ['Phase-by-phase trends', 'Opening performance', 'Mistake category tracking']
    }
  },
  coach: {
    eyebrow: 'Coaching & AI',
    title: 'A strategy coach that explains the why',
    description:
      "Go beyond “best move.” ChessWiz builds a guided strategy path from your own recent games and, on every move, compares your line to the engine’s best with plain-language explanations of king safety, piece activity, and pawn structure.",
    bullets: ['Guided strategy path', 'Your line vs. best line', 'AI explanations of every move']
  },
  tournaments: {
    eyebrow: 'Compete and review',
    title: 'Tournaments, reviewed',
    description:
      'Your tournament games, grouped and analyzed together. See how you performed across a whole event, which rounds cost you, and which openings held up under pressure.',
    bullets: ['Games grouped by event', 'Per-event performance', 'Spot rounds that cost you']
  },
  footer: {
    rights: '© 2026 ChessWiz. All rights reserved.',
    howItWorks: 'How it works',
    privacy: 'Privacy Policy',
    support: 'Support',
    terms: 'Terms of Service'
  },
  howItWorks: {
    title: 'How it works',
    backToGuides: 'All guides',
    backHome: 'Back to home'
  }
};

const pt: Translation = {
  localeLabel: 'Português',
  header: {
    tagline: 'Análise que transforma suas partidas em treino',
    howItWorks: 'Como funciona',
    privacy: 'Privacidade',
    support: 'Suporte',
    terms: 'Termos',
    signIn: 'Entrar',
    createAccount: 'Criar conta',
    languageLabel: 'Idioma'
  },
  hero: {
    chip: 'Feito a partir das suas próprias partidas',
    titleLine1: 'Melhore suas táticas e estratégia',
    titleLine2: 'usando as suas partidas.',
    subtitle:
      'O ChessWiz transforma cada partida em treino direcionado com alertas de erros, um coach de estratégia, puzzles personalizados, planos diários e análises que dizem no que focar a seguir.',
    getIos: 'Baixar app iOS',
    getAndroid: 'Baixar app Android',
    signInWeb: 'Entrar na web',
    createWebAccount: 'Criar conta na web'
  },
  featuresSection: {
    title: 'Treino que ataca o motivo das partidas escaparem',
    subtitle:
      'O ChessWiz transforma seus erros recorrentes, lacunas de abertura e chances perdidas em ciclos de prática que você realmente consegue repetir.'
  },
  features: {
    email: {
      eyebrow: 'Alertas de erros por e-mail e push',
      title: 'Alertas de erros por e-mail',
      description:
        'Receba e-mails e notificações push sobre os erros das suas partidas, com o lance, a variação de avaliação e a posição para saber exatamente o que corrigir.',
      bullets: ['Alertas por e-mail e push', 'Avaliação antes/depois', 'Foto da posição']
    },
    gamesList: {
      eyebrow: 'Cada partida, num relance',
      title: 'Histórico de partidas num relance',
      description:
        'Cada partida do seu histórico mostra a linha do tempo de avaliação, a precisão e a divisão por qualidade de lances para você achar as partidas que valem estudo sem abrir uma a uma.',
      bullets: ['Gráfico de avaliação embutido', 'Precisão e resultado', 'Contagem por qualidade de lance']
    },
    practiceHome: {
      eyebrow: 'Vários tipos de puzzle das suas partidas',
      title: 'Central de prática',
      description:
        'Aproveite vários tipos de puzzle baseados nas suas partidas, de erros do motor e treinos de avaliação a aberturas, sequências e lições aprendidas.',
      bullets: ['Puzzles de erros do motor', 'Treinos de avaliação', 'Prática de aberturas e sequências']
    },
    dailyTraining: {
      eyebrow: 'Um plano sob medida das suas últimas partidas',
      title: 'Treino diário',
      description:
        'Todo dia o ChessWiz monta uma sessão focada a partir das suas partidas mais recentes — os erros exatos, vitórias perdidas e momentos frágeis que valem revisar, reunidos num único baralho.',
      bullets: ['Baralho diário selecionado', 'Direto das suas partidas', 'Comece com um toque']
    },
    openingPractice: {
      eyebrow: 'Repetição em torno do seu repertório',
      title: 'Prática de aberturas',
      description:
        'Reforce ordens de lances e ideias das aberturas que você realmente joga, em vez de decorar teoria genérica.',
      bullets: ['Treinos de repertório', 'Memorização de linhas', 'Cobertura de variações']
    },
    puzzles: {
      eyebrow: 'Táticas de posições que você reconhece',
      title: 'Puzzles baseados nas suas partidas',
      description:
        'Gere puzzles a partir dos seus próprios erros, vitórias e chances perdidas para que o cálculo se conecte a partidas reais.',
      bullets: ['Conjuntos de puzzles personalizados', 'Recuperação de vitórias perdidas', 'Retenção de padrões']
    },
    analytics: {
      eyebrow: 'Acompanhe a evolução com sinais úteis',
      title: 'Análises que importam',
      description:
        'Veja quais aberturas, fases e padrões de erro estão te segurando e treine exatamente onde estão os pontos de rating a ganhar.',
      bullets: ['Tendências por fase', 'Desempenho por abertura', 'Acompanhamento por tipo de erro']
    }
  },
  coach: {
    eyebrow: 'Coaching e IA',
    title: 'Um coach de estratégia que explica o porquê',
    description:
      'Vá além do “melhor lance”. O ChessWiz monta um caminho de estratégia guiado a partir das suas partidas recentes e, a cada lance, compara a sua linha com a melhor do motor, com explicações claras sobre segurança do rei, atividade das peças e estrutura de peões.',
    bullets: ['Caminho de estratégia guiado', 'Sua linha vs. melhor linha', 'Explicações de IA em cada lance']
  },
  tournaments: {
    eyebrow: 'Compita e revise',
    title: 'Torneios, revisados',
    description:
      'Suas partidas de torneio, agrupadas e analisadas juntas. Veja seu desempenho no evento inteiro, quais rodadas custaram caro e quais aberturas resistiram à pressão.',
    bullets: ['Partidas agrupadas por evento', 'Desempenho por evento', 'Identifique rodadas que custaram caro']
  },
  footer: {
    rights: '© 2026 ChessWiz. Todos os direitos reservados.',
    howItWorks: 'Como funciona',
    privacy: 'Política de Privacidade',
    support: 'Suporte',
    terms: 'Termos de Serviço'
  },
  howItWorks: {
    title: 'Como funciona',
    backToGuides: 'Todos os guias',
    backHome: 'Voltar ao início'
  }
};

const es: Translation = {
  localeLabel: 'Español',
  header: {
    tagline: 'Análisis que convierte tus partidas en entrenamiento',
    howItWorks: 'Cómo funciona',
    privacy: 'Privacidad',
    support: 'Soporte',
    terms: 'Términos',
    signIn: 'Iniciar sesión',
    createAccount: 'Crear cuenta',
    languageLabel: 'Idioma'
  },
  hero: {
    chip: 'Creado a partir de tus propias partidas',
    titleLine1: 'Mejora tu táctica y estrategia',
    titleLine2: 'usando tus partidas.',
    subtitle:
      'ChessWiz convierte cada partida en entrenamiento específico con alertas de errores, un coach de estrategia, puzzles personalizados, planes diarios y analíticas que te dicen en qué trabajar a continuación.',
    getIos: 'Descargar app iOS',
    getAndroid: 'Descargar app Android',
    signInWeb: 'Iniciar sesión en la web',
    createWebAccount: 'Crear cuenta en la web'
  },
  featuresSection: {
    title: 'Entrenamiento que ataca por qué se escapan las partidas',
    subtitle:
      'ChessWiz convierte tus errores recurrentes, huecos de apertura y oportunidades perdidas en ciclos de práctica que de verdad puedes repetir.'
  },
  features: {
    email: {
      eyebrow: 'Alertas de errores por correo y push',
      title: 'Alertas de errores por correo',
      description:
        'Recibe correos y notificaciones push sobre los errores de tus partidas, con la jugada, el cambio de evaluación y la posición para saber exactamente qué corregir.',
      bullets: ['Alertas por correo y push', 'Evaluación antes/después', 'Captura de la posición']
    },
    gamesList: {
      eyebrow: 'Cada partida, de un vistazo',
      title: 'Historial de partidas de un vistazo',
      description:
        'Cada partida de tu historial muestra la línea de tiempo de evaluación, la precisión y el desglose por calidad de jugada para detectar las partidas que vale la pena estudiar sin abrirlas una a una.',
      bullets: ['Gráfico de evaluación integrado', 'Precisión y resultado', 'Conteo por calidad de jugada']
    },
    practiceHome: {
      eyebrow: 'Varios tipos de puzzle de tus partidas',
      title: 'Centro de práctica',
      description:
        'Disfruta varios tipos de puzzles basados en tus partidas, desde errores del motor y ejercicios de evaluación hasta aperturas, rachas y lecciones aprendidas.',
      bullets: ['Puzzles de errores del motor', 'Ejercicios de evaluación', 'Práctica de aperturas y rachas']
    },
    dailyTraining: {
      eyebrow: 'Un plan a medida de tus últimas partidas',
      title: 'Entrenamiento diario',
      description:
        'Cada día ChessWiz arma una sesión enfocada a partir de tus partidas más recientes: los errores exactos, victorias perdidas y momentos débiles que conviene repasar, reunidos en un solo mazo.',
      bullets: ['Mazo diario seleccionado', 'Directo de tus partidas', 'Empieza con un toque']
    },
    openingPractice: {
      eyebrow: 'Repetición en torno a tu repertorio',
      title: 'Práctica de aperturas',
      description:
        'Refuerza órdenes de jugadas e ideas de las aperturas que realmente juegas, en vez de memorizar teoría genérica.',
      bullets: ['Ejercicios de repertorio', 'Memorización de líneas', 'Cobertura de variantes']
    },
    puzzles: {
      eyebrow: 'Táctica de posiciones que reconoces',
      title: 'Puzzles basados en tus partidas',
      description:
        'Genera puzzles a partir de tus propios errores, victorias y oportunidades perdidas para que el cálculo se conecte con partidas reales.',
      bullets: ['Conjuntos de puzzles personalizados', 'Recuperación de victorias perdidas', 'Retención de patrones']
    },
    analytics: {
      eyebrow: 'Sigue tu progreso con señales útiles',
      title: 'Analíticas que importan',
      description:
        'Ve qué aperturas, fases y patrones de error te están frenando y entrena justo donde están los puntos de rating por ganar.',
      bullets: ['Tendencias por fase', 'Rendimiento por apertura', 'Seguimiento por tipo de error']
    }
  },
  coach: {
    eyebrow: 'Coaching e IA',
    title: 'Un coach de estrategia que explica el porqué',
    description:
      'Ve más allá de la “mejor jugada”. ChessWiz arma un camino de estrategia guiado a partir de tus partidas recientes y, en cada jugada, compara tu línea con la mejor del motor, con explicaciones claras sobre seguridad del rey, actividad de las piezas y estructura de peones.',
    bullets: ['Camino de estrategia guiado', 'Tu línea vs. la mejor línea', 'Explicaciones de IA en cada jugada']
  },
  tournaments: {
    eyebrow: 'Compite y revisa',
    title: 'Torneos, revisados',
    description:
      'Tus partidas de torneo, agrupadas y analizadas juntas. Mira tu rendimiento en todo el evento, qué rondas te costaron caro y qué aperturas aguantaron la presión.',
    bullets: ['Partidas agrupadas por evento', 'Rendimiento por evento', 'Detecta rondas que costaron caro']
  },
  footer: {
    rights: '© 2026 ChessWiz. Todos los derechos reservados.',
    howItWorks: 'Cómo funciona',
    privacy: 'Política de Privacidad',
    support: 'Soporte',
    terms: 'Términos de Servicio'
  },
  howItWorks: {
    title: 'Cómo funciona',
    backToGuides: 'Todas las guías',
    backHome: 'Volver al inicio'
  }
};

export const translations: Record<Locale, Translation> = { en, pt, es };

export const localeOrder: Locale[] = ['en', 'pt', 'es'];
